import type { FeedbackSurveyPlace } from '~/composables/unified/useFeedbackSurvey';

import { useActivityOrchestrator } from '~/composables/unified/useActivityOrchestrator';
import { useFeedbackSurvey } from '~/composables/unified/useFeedbackSurvey';
import { CoachActivityInfoType } from '~/graphql/generated/graphql';

interface ActivitySurveyScreenOptions {
  /** The place being asked about: a collection, or the activity itself. */
  place: Ref<FeedbackSurveyPlace>;
  /** The questions on screen; empty means there is nothing to ask here. */
  questions: Ref<unknown[]>;
  /** Whether the survey screen is the one showing. */
  isOpen: Ref<boolean>;
  /** What this viewer's externally-answered survey already paid, once redeemed. */
  externalCoinsAwarded: Ref<number | null>;
  /** Whether a teacher reads these answers, which is what makes a survey a lesson's. */
  sharedWithTeacher?: Ref<boolean>;
  /** The activity-info payload naming the place, built when an answer lands. */
  answeredInfo: (coinsAwarded: number) => Record<string, unknown>;
  /** Taking the skip button down closes the screen it sits on. */
  onSkip: () => void;
}

/**
 * Whether this tab has yet to report this student's external completion for this place. A reload
 * of the return page keeps `surveyReturn=1`, which the tab needs to close itself on the way out, and
 * reads the same redemption back, so the completion is claimed once per tab rather than once per
 * mount. The claim is the student's: session storage outlives a sign-out, so a second student
 * answering the same survey on a shared device in the same tab still reports theirs. Storage that
 * refuses the read or write reports anyway.
 */
const claimExternalCompletionReport = (userId: string | undefined, place: FeedbackSurveyPlace): boolean => {
  const placeKey = 'learningAssetId' in place
    ? `asset.${place.learningAssetId}`
    : `collection.${place.collectionId}`;
  const key = `feedbackSurvey.externalCompletedReported.${userId ?? 'anonymous'}.${placeKey}`;

  try {
    if (sessionStorage.getItem(key) !== null) {
      return false;
    }

    sessionStorage.setItem(key, '1');
  }
  catch {
    return true;
  }

  return true;
};

/**
 * The survey screen's behaviour, which a collection and a learning asset ask identically.
 *
 * Both surfaces show the same questionnaire, bank the same answer, tell the coach the same thing
 * and celebrate the same way; all that differs is the place being asked about. One copy is what
 * keeps a change to how a survey is asked from reaching only one of them.
 */
export const useActivitySurveyScreen = (options: ActivitySurveyScreenOptions) => {
  const orchestrator = useActivityOrchestrator();
  const { t } = useI18n();
  const { user } = useAuth();
  const coinsAwarded = ref<number | null>(null);
  const errorMessage = ref<string | null>(null);

  // Nothing a reader does in a preview is reported: they are looking at someone else's lesson.
  const reporting = computed(() => !orchestrator?.previewMode);

  const { submitting, submit, trackStarted, trackSkipped, trackExternalCompleted } = useFeedbackSurvey(
    options.place,
    computed(() => options.questions.value.length),
    options.sharedWithTeacher ?? computed(() => false),
  );

  const noteAnswered = (paid: number) => {
    orchestrator?.writeActivityInfoEvent?.({
      activityInfoType: CoachActivityInfoType.FeedbackSurveyAnswered,
      data: options.answeredInfo(paid),
    });
  };

  // `immediate` matters: with a warm urql cache the questions are already populated when this
  // watcher registers, so a lazy watcher would never see the transition and the started event
  // would be dropped on the most common path — arriving straight from a finish or a card.
  watch(() => options.isOpen.value && options.questions.value.length > 0, (asked) => {
    if (asked && reporting.value) {
      trackStarted();
    }
  }, { immediate: true });

  // The return leg lands here only after a claim that redeemed: any other visit to a survey
  // answered externally is a revisit, not a completion.
  const isSurveyReturn = useRoute().query.surveyReturn === '1';

  // An externally-answered survey has no questions to show: landing here with the redemption
  // already banked opens straight onto the thanks-and-coins overlay.
  watch([options.isOpen, options.externalCoinsAwarded], ([open, paid]) => {
    if (open && paid != null && coinsAwarded.value === null) {
      coinsAwarded.value = paid;
      noteAnswered(paid);

      if (isSurveyReturn && reporting.value && claimExternalCompletionReport(user.value?.id, options.place.value)) {
        trackExternalCompleted(paid);
      }
    }
  }, { immediate: true });

  const handleSubmitted = async (responses: Record<string, unknown>) => {
    errorMessage.value = null;
    const { submitted, coinsAwarded: paid } = await submit(responses);

    if (submitted) {
      coinsAwarded.value = paid;
      noteAnswered(paid);
      return;
    }

    errorMessage.value = t('unified.errors.genericFailed');
  };

  const handleSkipped = (answeredCount: number) => {
    if (reporting.value) {
      trackSkipped(answeredCount, 'skip_button');
    }

    options.onSkip();
  };

  const handleAbandoned = (answeredCount: number) => {
    if (reporting.value) {
      trackSkipped(answeredCount, 'closed');
    }
  };

  return {
    coinsAwarded,
    errorMessage,
    submitting,
    handleSubmitted,
    handleSkipped,
    handleAbandoned,
  };
};
